import './styles.sass';

import React, { useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import FeatureFlagProvider from 'components/Flipper/FeatureFlagProvider';
import AdvancedSearchContext from 'state/AdvancedSearchContext';
import SearchTerm from 'models/SearchTerm';

import ResearchOutput from 'components/Explorer/models/research_output';

import Header from './Header';
import TabBar from './TabBar';
import ContentPanel from './ContentPanel';
import DetailPage from './DetailPage';
import AdvancedSearch from './AdvancedSearch';
import useQueryParams from 'hooks/useQueryParams';

// In production nginx mounts the app under /explorer, so the browser URL is
// /explorer/<tab>. In test (cucumber/dev) it's served from the root. Detect
// which by inspecting the actual URL — v5's createBrowserHistory was lax about
// basename mismatches, v6's BrowserRouter refuses to route when they disagree.
// Exact-match the segment so paths like /explorer2 don't false-positive.
const detectBasename = () => {
  const p = window.location.pathname;
  return p === '/explorer' || p.startsWith('/explorer/') ? '/explorer' : '';
};

const ExplorerShell = ({ instanceLogo, instanceName }) => {
  const params = useQueryParams();

  const [advancedSearchVisible, setAdvancedSearchVisible] = useState(false);
  const [advancedSearchTerm, setAdvancedSearchTerm] = useState(SearchTerm.empty());

  const clearSearchTerm = () => setAdvancedSearchTerm(SearchTerm.empty());

  const showAdvancedSearch = ({ initialSearchTerm } = {}) => {
    if (initialSearchTerm) {
      setAdvancedSearchTerm(initialSearchTerm);
    } else {
      clearSearchTerm();
    }
    setAdvancedSearchVisible(true);
  };

  const hideAdvancedSearch = () => {
    setAdvancedSearchVisible(false);
    clearSearchTerm();
  };

  const detailPageOutput = params.showDetails ? new ResearchOutput({ id: params.showDetails }) : null;

  return (
    <AdvancedSearchContext.Provider value={{ showAdvancedSearch }}>
      <div className="Explorer">
        <div className="Explorer-content-wrapper" {...{ inert: detailPageOutput !== null ? '' : undefined }}>
          <Header
            logo={instanceLogo}
            instanceName={instanceName}
            onShowAdvancedSearch={showAdvancedSearch}
            onSelectShowMore={(searchTermFromQuickSearch) =>
              showAdvancedSearch({ initialSearchTerm: searchTermFromQuickSearch })
            }
          />
          <div className="Explorer-content-panel-wrapper">
            {window.current_user.librarian ? null : <TabBar />}
            <ContentPanel onRouteChangeUpdate={window.Analytics.pageChange} />
          </div>
        </div>
        {detailPageOutput && <DetailPage output={detailPageOutput} />}
        <div className="Explorer-advanced-search-wrapper">
          {advancedSearchVisible && (
            <AdvancedSearch key="advancedSearch" onClose={hideAdvancedSearch} initialSearchTerm={advancedSearchTerm} />
          )}
        </div>
      </div>
    </AdvancedSearchContext.Provider>
  );
};

const Explorer = ({ instanceLogo, instanceName }) => {
  const [basename] = useState(detectBasename);

  return (
    <BrowserRouter basename={basename}>
      <FeatureFlagProvider>
        <ExplorerShell instanceLogo={instanceLogo} instanceName={instanceName} />
      </FeatureFlagProvider>
    </BrowserRouter>
  );
};

export default Explorer;
